import { formatDateRu } from "../../../utils/appointmentLabels";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import { membershipStatusMeta } from "../../../utils/clientMembershipUtils";
import ClientPayMembershipButton from "./ClientPayMembershipButton";
import { Button, useToast } from "../../ui";
import { useClientAthlete } from "../../../context/ClientAthleteContext";
import { dispatchClientRefresh } from "../../../hooks/useClientRefresh";
import s from "./clientApp.module.css";

function visitsLeft(membership, stats) {
  const mv = stats?.membership_visits;
  if (mv?.is_visit_based && mv.remaining != null) return Number(mv.remaining);
  if (membership?.visits_remaining != null) return Number(membership.visits_remaining);
  return null;
}

function planKind(plan) {
  if (plan?.plan_type === "visits") return "По занятиям";
  if (plan?.plan_type === "time") return "По времени";
  return null;
}

function planShopMeta(plan) {
  const parts = [];
  if (plan.plan_type === "visits") {
    parts.push(`${plan.visits_count || "—"} занятий`);
    if (plan.duration_days) parts.push(`${plan.duration_days} дней`);
  } else if (plan.duration_days) {
    parts.push(`${plan.duration_days} дней`);
  }
  return parts.join(" · ");
}

function Fact({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <div className={s.profClubFactTile}>
      <span className={s.profClubFactTileLbl}>{label}</span>
      <span className={s.profClubFactTileVal}>{value}</span>
    </div>
  );
}

function MembershipCard({
  membership,
  stats,
  showFacts,
  patientId,
  onRenew,
  showRenew,
  renewBusy,
  onCancelled,
}) {
  const status = membershipStatusMeta(membership.status, membership.notes);
  const plan = membership.plan;
  const left = visitsLeft(membership, stats);
  const kind = planKind(plan);

  const period =
    membership.starts_at && membership.ends_at
      ? `${formatDateRu(membership.starts_at)} — ${formatDateRu(membership.ends_at)}`
      : membership.ends_at
        ? `до ${formatDateRu(membership.ends_at)}`
        : membership.starts_at
          ? `с ${formatDateRu(membership.starts_at)}`
          : null;

  const visitsVal =
    left != null
      ? `${left}`
      : plan?.plan_type === "time"
        ? "безлимит"
        : null;

  return (
    <article className={`${s.profClubMem} ${showFacts ? s.profClubMemActive : ""}`}>
      <div className={s.profClubMemHead}>
        <div className={s.profClubMemTitles}>
          <h3>{plan?.name || "Абонемент"}</h3>
          {kind ? <p className={s.profClubMemSub}>{kind}</p> : null}
        </div>
        <span className={`${s.profClubStatus} ${s[`profClubStatus_${status.tone}`]}`}>
          {status.label}
        </span>
      </div>

      {showFacts ? (
        <div className={s.profClubFactGrid} style={{ marginTop: 12 }}>
          <Fact label="Занятия" value={visitsVal} />
          <Fact label="Период" value={period} />
        </div>
      ) : null}

      <ClientPayMembershipButton
        membership={membership}
        patientId={patientId}
        onCancelled={onCancelled}
      />

      {showRenew ? (
        <Button
          type="button"
          variant="secondary"
          onClick={onRenew}
          disabled={renewBusy}
          className={s.profClubBtn}
        >
          {renewBusy ? "Переход к оплате…" : "Продлить"}
        </Button>
      ) : null}
    </article>
  );
}

export default function ClientClubMembership({ dash, patientId, onChanged }) {
  const toast = useToast();
  const shopRef = useRef(null);
  const { athleteQueryParam } = useClientAthlete();
  const membership = dash?.membership;
  const stats = dash?.stats;

  const [catalog, setCatalog] = useState({
    plans: [],
    purchase: null,
    loading: true,
  });
  const [buyingId, setBuyingId] = useState(null);

  const pendingList = useMemo(() => {
    const rows = dash?.memberships || [];
    return rows.filter((m) => m?.status === "pending" && m.id !== membership?.id);
  }, [dash?.memberships, membership?.id]);

  const loadPlans = useCallback(async () => {
    setCatalog((c) => ({ ...c, loading: true }));
    try {
      const res = await clinicAPI.getMyMembershipPlans(athleteQueryParam || {});
      setCatalog({
        plans: res.plans || [],
        purchase: res.purchase || null,
        loading: false,
      });
    } catch {
      setCatalog({ plans: [], purchase: null, loading: false });
    }
  }, [athleteQueryParam]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  // Fallback: если webhook не дошёл — сверяем pending оплаты при открытии экрана
  useEffect(() => {
    let cancelled = false;
    clinicAPI
      .syncPendingOnlineMembershipPayments({
        patient_id: patientId || undefined,
      })
      .then((res) => {
        if (cancelled || !(res?.confirmed > 0)) return;
        toast.success("Оплата найдена — абонемент обновлён");
        onChanged?.();
        dispatchClientRefresh();
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [patientId, onChanged, toast]);

  const purchase = catalog.purchase;
  const mode = purchase?.mode;
  const isRenew = mode === "renew" || mode === "topup" || mode === "renewal";
  const shopPlans = catalog.plans || [];
  const visitKind = purchase?.kind === "visits" || mode === "topup";

  const preferredPlan = useMemo(() => {
    if (!shopPlans.length) return null;
    const currentId = membership?.plan_id || membership?.plan?.id;
    if (currentId) {
      const same = shopPlans.find((p) => String(p.id) === String(currentId));
      if (same) return same;
    }
    return shopPlans[0];
  }, [shopPlans, membership?.plan_id, membership?.plan?.id]);

  const buyPlan = async (plan) => {
    if (!plan?.id) return;
    setBuyingId(plan.id);
    try {
      // Один запрос: абонемент + платёж транзакционно; при ошибке Init бэкенд откатывает оба
      const purchased = await clinicAPI.purchaseMyMembership(
        { plan_id: plan.id },
        athleteQueryParam || {},
      );
      if (
        purchased.status === "confirmed" ||
        purchased.membership_status === "active"
      ) {
        toast.success("Оплата найдена — абонемент обновлён");
        onChanged?.();
        dispatchClientRefresh();
        loadPlans();
        window.setTimeout(() => window.location.reload(), 400);
        return;
      }
      if (purchased.payment_url) {
        window.location.href = purchased.payment_url;
        return;
      }
      // Fallback для старых ответов API без payment_url
      const membershipId = purchased.membership?.id;
      if (membershipId) {
        const pay = await clinicAPI.initOnlineMembershipPayment({
          membership_id: membershipId,
          patient_id: patientId || undefined,
        });
        if (pay.status === "confirmed") {
          toast.success("Оплата найдена — абонемент обновлён");
          onChanged?.();
          dispatchClientRefresh();
          loadPlans();
          window.setTimeout(() => window.location.reload(), 400);
          return;
        }
        if (pay.payment_url) {
          window.location.href = pay.payment_url;
          return;
        }
      }
      toast.error("Не получена ссылка на оплату");
      onChanged?.();
      dispatchClientRefresh();
      loadPlans();
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось начать оплату");
      onChanged?.();
      dispatchClientRefresh();
      loadPlans();
    } finally {
      setBuyingId(null);
    }
  };

  const renewActive = () => {
    if (!preferredPlan) {
      toast.error("Нет тарифа для продления");
      return;
    }
    buyPlan(preferredPlan);
  };

  const previewLine = (plan) => {
    const p = plan.renew_preview;
    if (!p) return null;
    const bits = [];
    if (p.visits_after != null) bits.push(`${p.visits_after} занятий`);
    if (p.ends_at_after) bits.push(`до ${formatDateRu(p.ends_at_after)}`);
    return bits.length ? `Станет: ${bits.join(", ")}` : null;
  };

  return (
    <div className={s.profClub}>
      <section className={s.profClubSection}>
        <h2 className={s.profClubSectionTitle}>Сейчас</h2>
        {!membership && pendingList.length === 0 ? (
          <p className={s.profMuted}>Нет активного абонемента</p>
        ) : (
          <div className={s.profClubStack}>
            {membership ? (
              <MembershipCard
                membership={membership}
                stats={stats}
                showFacts
                patientId={patientId}
                showRenew={
                  Boolean(isRenew && preferredPlan) &&
                  membership.status === "active"
                }
                renewBusy={Boolean(buyingId)}
                onRenew={renewActive}
                onCancelled={onChanged}
              />
            ) : null}
            {pendingList.map((m) => (
              <MembershipCard
                key={m.id}
                membership={m}
                stats={null}
                showFacts={false}
                patientId={patientId}
                onCancelled={onChanged}
              />
            ))}
          </div>
        )}
      </section>

      <section className={s.profClubSection} ref={shopRef} id="club-membership-shop">
        <h2 className={s.profClubSectionTitle}>
          {isRenew ? "Продлить" : "Тарифы"}
        </h2>
        {visitKind && isRenew ? (
          <p className={s.profMuted} style={{ marginBottom: 12 }}>
            Занятия и срок суммируются с текущим
          </p>
        ) : purchase?.starts_at && isRenew ? (
          <p className={s.profMuted} style={{ marginBottom: 12 }}>
            Новый период с {formatDateRu(purchase.starts_at)}
          </p>
        ) : null}

        {catalog.loading ? (
          <p className={s.profMuted}>Загрузка…</p>
        ) : shopPlans.length === 0 ? (
          <p className={s.profMuted}>Нет тарифов для онлайн-оплаты</p>
        ) : (
          <div className={s.profClubStack}>
            {shopPlans.map((plan) => (
              <article key={plan.id} className={s.profClubMem}>
                <div className={s.profClubMemHead}>
                  <div className={s.profClubMemTitles}>
                    <h3>{plan.name}</h3>
                    <p className={s.profClubMemSub}>{planShopMeta(plan)}</p>
                  </div>
                  <strong>{Number(plan.price).toLocaleString("ru-RU")} ₽</strong>
                </div>
                {previewLine(plan) ? (
                  <p className={s.profMuted} style={{ margin: "8px 0 0" }}>
                    {previewLine(plan)}
                  </p>
                ) : null}
                <Button
                  type="button"
                  onClick={() => buyPlan(plan)}
                  disabled={Boolean(buyingId)}
                  className={s.profClubBtn}
                >
                  {buyingId === plan.id
                    ? "Переход к оплате…"
                    : isRenew
                      ? "Продлить"
                      : "Купить"}
                </Button>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
